import { useLayoutEffect, useRef } from "react";
import type { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from "axios";
import { AxiosHeaders } from "axios";
import { acquireToken } from "@/authentication/TokenService";
import { Configuration } from "@/config";

interface InterceptorIds {
  request: number;
  response: number;
}

type RetryableConfig = InternalAxiosRequestConfig & { retryAttempted?: boolean };

/**
 * Attaches request and response interceptors to the given axios instances.
 * Handles bearer-token injection, Customer-Id header, and 401 retry/refresh.
 *
 * Signature mirrors `useApiInterceptors` from `@pcs/shared` so this file can be
 * deleted and its callers can switch their import to `@pcs/shared` once
 * `Pcs.Authorization.Web` is merged into the mono.ui workspace.
 */
function useApiInterceptors(axiosInstances: AxiosInstance[]): void {
  const interceptorIdsRef = useRef<Map<AxiosInstance, InterceptorIds>>(new Map());

  useLayoutEffect(() => {
    const ids = new Map<AxiosInstance, InterceptorIds>();
    const { config } = new Configuration();
    const customerId = config.PCS_SOFTWARE_CUSTOMER_ID;

    axiosInstances.forEach((instance) => {
      const requestId = instance.interceptors.request.use(
        async (req: InternalAxiosRequestConfig) => {
          const token = await acquireToken(false);
          if (req.headers instanceof AxiosHeaders) {
            req.headers.set("Authorization", `Bearer ${token}`);
            req.headers.set("Customer-Id", String(customerId));
            if (!req.headers.get("Content-Type")) {
              req.headers.set("Content-Type", "application/json");
            }
          } else {
            const h = (req.headers ?? {}) as Record<string, string>;
            h.Authorization = `Bearer ${token}`;
            h["Customer-Id"] = String(customerId);
            if (!h["Content-Type"]) h["Content-Type"] = "application/json";
            req.headers = h as unknown as InternalAxiosRequestConfig["headers"];
          }
          return req;
        },
        (error: AxiosError) => Promise.reject(error),
      );

      const responseId = instance.interceptors.response.use(
        (response) => response,
        async (error: AxiosError) => {
          const original = error.config as RetryableConfig | undefined;
          if (
            error.response?.status === 401 &&
            original &&
            !original.retryAttempted
          ) {
            original.retryAttempted = true;
            try {
              const token = await acquireToken(true);
              if (original.headers instanceof AxiosHeaders) {
                original.headers.set("Authorization", `Bearer ${token}`);
              } else {
                const h = (original.headers ?? {}) as Record<string, string>;
                h.Authorization = `Bearer ${token}`;
                original.headers =
                  h as unknown as InternalAxiosRequestConfig["headers"];
              }
              return await instance(original);
            } catch (refreshError) {
              return Promise.reject(refreshError);
            }
          }
          return Promise.reject(error);
        },
      );

      ids.set(instance, { request: requestId, response: responseId });
    });

    interceptorIdsRef.current = ids;

    return () => {
      ids.forEach(({ request, response }, instance) => {
        instance.interceptors.request.eject(request);
        instance.interceptors.response.eject(response);
      });
    };
  }, [axiosInstances]);
}

export default useApiInterceptors;
